import { Injectable } from '@angular/core';
import {
  ContactChangeChannel,
  ContactChangeResponse,
  ContactChangeStatus,
  ContactChangeType,
  InitiateContactChangeRequest,
  ResendContactCodeRequest,
  UpdateNewContactValueRequest,
  VerifyContactRequest,
} from '@models';
import { Observable, of, throwError } from 'rxjs';
import { ContactChangeService } from './contact-change.service';

const MOCK_VALID_CODE = '123456';

@Injectable({ providedIn: 'root' })
export class ContactChangeMockService implements ContactChangeService {
  private mockId = 'mock-contact-change-id';
  private mockType: ContactChangeType = 'Phone';
  private mockChannel: ContactChangeChannel = 'Phone';
  private mockStatus: ContactChangeStatus = 'PendingCurrentVerification';
  private mockNewValue = '';

  initiate(
    req: InitiateContactChangeRequest
  ): Observable<ContactChangeResponse> {
    this.mockType = req.type;
    this.mockChannel = req.channel ?? req.type;
    this.mockStatus = 'PendingCurrentVerification';
    return of(this.buildResponse());
  }

  getStatus(_id: string): Observable<ContactChangeResponse> {
    return of(this.buildResponse());
  }

  verifyCurrent(
    _id: string,
    req: VerifyContactRequest
  ): Observable<ContactChangeResponse> {
    if (req.code !== MOCK_VALID_CODE) {
      return throwError(() => ({
        status: 400,
        error: { code: 'invalid_code' },
      }));
    }
    this.mockStatus = 'PendingNewValue';
    return of(this.buildResponse());
  }

  updateNewValue(
    _id: string,
    req: UpdateNewContactValueRequest
  ): Observable<ContactChangeResponse> {
    this.mockNewValue = req.newValue;
    this.mockStatus = 'PendingNewVerification';
    return of(this.buildResponse());
  }

  verifyNew(
    _id: string,
    req: VerifyContactRequest
  ): Observable<ContactChangeResponse> {
    if (req.code !== MOCK_VALID_CODE) {
      return throwError(() => ({
        status: 400,
        error: { code: 'invalid_code' },
      }));
    }
    this.mockStatus = 'Completed';
    return of(this.buildResponse());
  }

  resend(
    _id: string,
    req: ResendContactCodeRequest
  ): Observable<ContactChangeResponse> {
    if (
      req.fallbackChannel &&
      this.mockStatus === 'PendingCurrentVerification'
    ) {
      this.mockChannel = req.fallbackChannel;
    }

    return of(this.buildResponse());
  }

  cancel(_id: string): Observable<ContactChangeResponse> {
    this.mockStatus = 'Cancelled';
    return of(this.buildResponse());
  }

  private buildResponse(): ContactChangeResponse {
    return {
      id: this.mockId,
      type: this.mockType,
      status: this.mockStatus,
      currentChannel: this.mockChannel,
      newChannel: this.mockNewValue ? this.mockType : null,
      maskedNewValue: this.mockNewValue
        ? this.mockType === 'Email'
          ? this.mockNewValue.replace(/^(.).*(@.*)$/, '$1***$2')
          : this.mockNewValue.replace(
              /(\+?\d{1,3})\d+(\d{4})$/,
              '$1 (***) ***-$2'
            )
        : undefined,
      expiresAtUtc: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    };
  }
}
